import { useState } from "react";
import { motion } from "framer-motion";
import { SNAKE_LEVELS } from "@/utils/snakeGameData";
import { Home, Play, Star } from "lucide-react";
import { Button } from "@/components/ui/button";

const SnakeGameSettings = ({ onStartGame, onBack }) => {
  const [selectedLevel, setSelectedLevel] = useState(null); // null = all levels

  const MODE_OPTIONS = [
    { id: null, name: "Semua Level", icon: "🌟", desc: "Main dari Level 1 sampai 6" },
    ...SNAKE_LEVELS.map(l => ({
      id: l.id,
      name: `Lv${l.id} ${l.name}`,
      icon: l.icon,
      desc: l.desc,
      color: l.color,
    })),
  ];

  return (
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="w-full max-w-2xl mx-auto"
    >
      <div className="glass-card-strong rounded-2xl p-5 sm:p-6 border border-gold/20"
        style={{ boxShadow: '0 0 30px rgba(74,222,128,0.1)' }}
      >
        <div className="text-center mb-5">
          <motion.span className="text-4xl block mb-2"
            animate={{ rotate: [0, -5, 5, 0], y: [0, -5, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >🐍</motion.span>
          <h2 className="font-fredoka text-xl sm:text-2xl font-bold text-green-400 mb-1">
            Ular Kata Ceria
          </h2>
          <p className="font-fredoka text-gold-light/80 text-sm">
            Arahkan ular ke huruf & angka yang benar!
          </p>
        </div>

        <div className="space-y-2 mb-5">
          {MODE_OPTIONS.map((opt, i) => {
            const isActive = selectedLevel === opt.id;
            const color = opt.color || "#4ade80";
            return (
              <motion.button
                key={opt.id ?? "all"}
                data-testid={`snake-level-${opt.id ?? "all"}`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                onClick={() => setSelectedLevel(opt.id)}
                className={`w-full flex items-center gap-3 rounded-xl p-3 border-2 text-left transition-all ${
                  isActive ? "shadow-lg" : ""
                }`}
                style={{
                  borderColor: isActive ? color : `${color}25`,
                  background: isActive ? `${color}15` : 'transparent',
                }}
              >
                <span className="text-xl w-8 text-center">{opt.icon}</span>
                <div className="flex-1">
                  <p className="font-fredoka font-bold text-sm" style={{ color }}>{opt.name}</p>
                  <p className="font-fredoka text-gold-light/80/70 text-xs">{opt.desc}</p>
                </div>
                {isActive && (
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
                    <Star className="w-5 h-5 fill-current" style={{ color }} />
                  </motion.div>
                )}
              </motion.button>
            );
          })}
        </div>

        <div className="flex flex-col gap-2">
          <Button
            data-testid="snake-start-btn"
            onClick={() => onStartGame(selectedLevel)}
            className="w-full font-fredoka font-bold text-brown-dark bg-green-400 hover:bg-green-500 text-lg py-5"
          >
            <Play className="w-5 h-5 mr-2" />Mulai Petualangan!
          </Button>
          <Button
            onClick={onBack}
            variant="outline"
            className="w-full border-gold/20 text-pink-300 hover:bg-gold/10 font-fredoka"
            data-testid="snake-settings-back-btn"
          >
            <Home className="w-4 h-4 mr-2" />Kembali ke Menu Utama
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default SnakeGameSettings;
